import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { IonToggle } from '@ionic/react';
import { Card, SectionTitle, Skeleton, Stat } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { RequestCard, RideCard } from '../../components/TripCards';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import type { RideDto, RideRequestDto } from '../../lib/types';
import { useAuth } from '../../stores/auth';
import { useUnreadNotifications } from '../common/NotificationsPage';
import { ComplianceBanner } from './documents/DocumentsPage';
import { useDriverMe } from './DriverProfilePage';
import { fullScreenAlertAllowed, openFullScreenAlertSettings } from '../../lib/fullscreen-alert';
import { useReportSummary } from './ReportsPage';
import { formatMoney } from '@tcrd/shared';
import type { Compliance } from './documents/types';
import { DriverHeroCard } from '../../components/DriverHeroCard';
import { Avatar } from '@tcrd/ui';
import { DestinationModeCard, WeeklyGoalCard } from './DriverTools';
import { CommissionBlockedNotice, useMyCommissions } from './CommissionsPage';

export function DriverDashboard() {
  const user = useAuth((s) => s.user);
  const toast = useToast();
  const me = useDriverMe();
  const unread = useUnreadNotifications();
  // El panel se refresca cada 10 s mientras está en pantalla (solicitudes, viajes y ganancias).
  const REFRESH = 10_000;
  const requests = useQuery({ queryKey: ['ride-requests', 'inbox', 'new'], queryFn: () => api<RideRequestDto[]>('/ride-requests', { query: { status: 'REQUESTED,INFO_REQUESTED' } }), refetchInterval: REFRESH });
  const upcoming = useQuery({ queryKey: ['rides', 'upcoming'], queryFn: () => api<RideDto[]>('/rides', { query: { scope: 'upcoming' } }), refetchInterval: REFRESH });
  // Android 14+: el permiso de avisos a pantalla completa lo activa el usuario. Se revisa al volver a la app.
  const fullScreen = useQuery({ queryKey: ['fullscreen-alert'], queryFn: fullScreenAlertAllowed, refetchOnWindowFocus: true, staleTime: 0 });
  const report = useReportSummary(undefined, undefined, REFRESH);
  const clients = useQuery({ queryKey: ['driver-clients'], queryFn: () => api<{ summary: { total: number; newThisMonth: number } }>('/drivers/me/clients') });
  const compliance = useQuery({ queryKey: ['compliance'], queryFn: () => api<Compliance>('/documents/compliance') });
  const commissions = useMyCommissions();
  const expiring = compliance.data?.warnings.filter((w) => w.status === 'EXPIRING_SOON').length ?? 0;
  const active = upcoming.data?.filter((r) => ['DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'].includes(r.status)) ?? [];
  const today = upcoming.data?.filter((r) => new Date(r.scheduledAt).toDateString() === new Date().toDateString()) ?? [];

  const togglePresence = async (on: boolean) => {
    try {
      await api('/drivers/me/presence', { method: 'PATCH', body: { presence: on ? 'AVAILABLE' : 'OFFLINE' } });
      await me.refetch();
    } catch (e) {
      toast(errorMessage(e), 'danger');
      // El interruptor vuelve a su estado real (p. ej. cuenta pausada por comisión vencida).
      await Promise.all([me.refetch(), commissions.refetch()]);
    }
  };

  return (
    <Page
      title="Panel"
      logo="left"
      onRefresh={() => Promise.all([requests.refetch(), upcoming.refetch(), me.refetch()])}
      actions={
        <span className="flex items-center gap-1 pr-2">
          <Link to="/driver/notifications" className="tap relative flex items-center justify-center px-2" aria-label="Notificaciones">
            <Bell className="size-6" />
            {!!unread.data?.count && <span className="absolute top-1 right-1 size-2.5 rounded-full bg-brand-red" aria-hidden />}
          </Link>
          <Link to="/driver/profile" className="tap rounded-full" aria-label="Mi perfil">
            <Avatar src={me.data?.photoUrl} firstName={user?.firstName} lastName={user?.lastName} size={36} />
          </Link>
        </span>
      }
    >
      <div className="flex flex-col gap-5">
        {commissions.data?.blocked && <CommissionBlockedNotice amount={commissions.data.blocked.amount} link />}
        {me.data && (
          <DriverHeroCard
            d={{ ...me.data, rating: me.data.rating.average }}
            status={
              <label className={`self-start flex items-center gap-2 rounded-full pl-3 pr-1 py-0.5 text-xs font-semibold ${me.data.presence === 'OFFLINE' ? 'bg-[var(--surface-2)] text-[var(--text-muted)]' : 'bg-green-600 text-white'}`}>
                {me.data.presence === 'AVAILABLE' ? 'En línea' : me.data.presence === 'BUSY' ? 'En un viaje' : 'Desconectado'}
                <IonToggle color="light" checked={me.data.presence !== 'OFFLINE'} onIonChange={(e) => void togglePresence(e.detail.checked)} aria-label="Disponibilidad" className="scale-75" />
              </label>
            }
          />
        )}
        <ComplianceBanner />
        {fullScreen.data === false && (
          <Card className="p-4 border border-amber-300 bg-amber-50 dark:bg-amber-500/10">
            <p className="text-sm font-semibold">Activa los avisos a pantalla completa</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Para que las solicitudes nuevas aparezcan aunque el teléfono esté bloqueado, como una llamada.</p>
            <button className="tap mt-2 text-sm font-semibold text-brand-red" onClick={() => void openFullScreenAlertSettings()}>
              Activar ahora
            </button>
          </Card>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Link to="/driver/requests"><Stat label="Solicitudes nuevas" value={requests.data?.length ?? '—'} tone={requests.data?.length ? 'accent' : 'default'} /></Link>
          <Link to="/driver/calendar"><Stat label="Viajes de hoy" value={today.length} /></Link>
          <Stat label="Viajes activos" value={active.length} />
          <Link to="/driver/reports"><Stat label="Ingresos hoy" value={formatMoney(report.data?.quick.today.fare ?? '0')} hint={report.data ? `+ ${formatMoney(report.data.quick.today.tips)} propinas` : undefined} /></Link>
          <Link to="/driver/reports"><Stat label="Esta semana" value={formatMoney(report.data?.quick.week.fare ?? '0')} /></Link>
          <Link to="/driver/reports"><Stat label="Este mes" value={formatMoney(report.data?.quick.month.fare ?? '0')} hint={report.data ? `Propinas ${formatMoney(report.data.quick.month.tips)}` : undefined} /></Link>
          <Link to="/driver/payments"><Stat label="Cobros pendientes" value={formatMoney(report.data?.pending.amount ?? '0')} tone={report.data?.pending.count ? 'warn' : 'default'} /></Link>
          <Link to="/driver/clients"><Stat label="Mis clientes" value={clients.data?.summary.total ?? '—'} hint={clients.data?.summary.newThisMonth ? `+${clients.data.summary.newThisMonth} este mes` : undefined} /></Link>
          <Link to="/driver/documents"><Stat label="Documentos por vencer" value={expiring} tone={expiring ? 'warn' : 'default'} /></Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <WeeklyGoalCard refetchInterval={REFRESH * 3} />
          <DestinationModeCard />
        </div>

        {active.map((r) => (
          <section key={r.id}>
            <SectionTitle title="En curso" />
            <RideCard r={r} href={`/driver/rides/${r.id}/live`} />
          </section>
        ))}

        <section>
          <SectionTitle title="Solicitudes por cotizar" action={<Link to="/driver/requests" className="text-sm font-medium text-brand-red">Ver todas</Link>} />
          {requests.isLoading && <Skeleton className="h-28" />}
          {requests.data?.length === 0 && <Card className="p-4 text-sm text-[var(--text-muted)]">Sin solicitudes pendientes.</Card>}
          <div className="flex flex-col gap-3">{requests.data?.slice(0, 3).map((r) => <RequestCard key={r.id} r={r} href={`/driver/requests/${r.id}`} />)}</div>
        </section>

        <section>
          <SectionTitle title="Próximas reservas" action={<Link to="/driver/calendar" className="text-sm font-medium text-brand-red">Agenda</Link>} />
          {upcoming.data?.length === 0 && <Card className="p-4 text-sm text-[var(--text-muted)]">No tienes reservas próximas.</Card>}
          <div className="flex flex-col gap-3">{upcoming.data?.filter((r) => r.status === 'CONFIRMED').slice(0, 5).map((r) => <RideCard key={r.id} r={r} href={`/driver/rides/${r.id}`} />)}</div>
        </section>
      </div>
    </Page>
  );
}
